import { Injectable } from 'framework/di';

import { Product as BoostProduct } from '../../providers/boost';
import { Product as ShopifyProduct } from '../../providers/shopify';
import { AppService } from '../app/app.service';
import { CartAPI } from '../cart/cart.api';
import { PlatformLoader } from '../platform/platform.loader';
import { RecentlyViewedProductService } from '../recently-viewed-product/recently-viewed-product.service';
import { TemplateAPI } from '../template/template.api';
import { RecommendationAPI } from './recommendation.api';
import { RecommendationModel, SupportedPage } from './recommendation.model';

/**
 * Service for managing recommendation widgets and their data.
 */
@Injectable
export class RecommendationService {
  constructor(
    private appService: AppService,
    private platformLoader: PlatformLoader,
    private templateAPI: TemplateAPI,
    private recentlyViewedProductService: RecentlyViewedProductService,
    private cartAPI: CartAPI,
    private recommendationAPI: RecommendationAPI
  ) {}

  get widgets() {
    return this.appService.TAEAppConfig.recommendationWidgets || {};
  }

  get defaultWidgetSettings() {
    return this.appService.TAEAppConfig.recommendationWidgets?.defaultSettings || {};
  }

  /**
   * Identifies which page type a widget belongs to based on its ID.
   *
   * @param payload - Object containing the widget ID
   * @returns The page type or undefined if not recognized
   */
  identifyWidgetPage(payload: { widgetId: string }): SupportedPage | undefined {
    const { widgetId } = payload;
    if (widgetId.startsWith('homepage')) return 'home-page';
    if (widgetId.startsWith('cartpage')) return 'cart-page';
    if (widgetId.startsWith('collectionpage')) return 'collection-page';
    if (widgetId.startsWith('productpage')) return 'product-page';
  }

  /**
   * Validates whether a widget should be rendered based on configuration and page context.
   *
   * Checks widget ID, page type, widget configuration, settings, and template availability.
   *
   * @param payload - Object containing the widget ID
   * @returns True if widget is valid and should be rendered
   */
  validateWidget(
    payload: Partial<{
      widgetId?: string;
    }>
  ): boolean {
    const { widgetId } = payload;

    if (!widgetId || !widgetId.trim()) {
      return false;
    }

    const widgetPage = this.identifyWidgetPage({ widgetId });
    if (!widgetPage) {
      return false;
    }

    const { page } = this.platformLoader.platform.router;

    if (widgetPage === 'home-page' && page !== 'home') return false;
    if (widgetPage === 'collection-page' && page !== 'collection') return false;
    if (widgetPage === 'product-page' && page !== 'product') return false;
    if (widgetPage === 'cart-page' && page !== 'cart') return false;

    if (Object.keys(this.widgets).length === 0) return false;

    const widgetsInPage = this.widgets[widgetPage];
    if (!widgetsInPage || Object.keys(widgetsInPage).length === 0) return false;

    const widget = widgetsInPage[widgetId];
    if (!widget) return false;

    const defaultSettings = this.defaultWidgetSettings;
    const widgetSettings = {
      ...defaultSettings,
      ...widget.widgetDesignSettings,
    };

    if (!widgetSettings || Object.keys(widgetSettings).length === 0) return false;

    const widgetParams = widget.params;
    if (!widgetParams || Object.keys(widgetParams).length === 0) return false;

    // If there is no templateMetaData => do nothing
    const { templateMetadata } = this.appService.TAEAppConfig;
    if (!templateMetadata || Object.keys(templateMetadata).length === 0) return false;

    return true;
  }

  /**
   * Retrieves the widget template from TAE config or API.
   *
   * @returns Promise resolving to the template string
   */
  async getWidgetTemplate(): Promise<string> {
    const templateFromTAE = this.appService.TAEAppConfig.template?.recommendation;
    if (templateFromTAE) {
      return templateFromTAE;
    }

    const { templateMetadata } = this.appService.TAEAppConfig;
    const templateId = templateMetadata?.recommendation;

    const { templates } = await this.templateAPI.getWidgetTemplate({
      name: 'recommendation',
      id: templateId,
    });

    return templates[templateId];
  }

  /**
   * Retrieves product IDs for recently viewed products.
   *
   * This method can be overridden to customize which recently viewed products
   * should be used for recommendations (e.g., filtering by category or availability).
   *
   * @returns Promise resolving to array of product IDs
   *
   * @example
   * // Override to filter recently viewed products
   * protected async getRecentlyViewedProductIds() {
   *   const productIds = await super.getRecentlyViewedProductIds();
   *   // Only return products from last 7 days
   *   return this.filterRecentProducts(productIds, 7);
   * }
   */
  protected async getRecentlyViewedProductIds(): Promise<(string | number)[]> {
    return this.recentlyViewedProductService.getRecentlyViewedProducts();
  }

  /**
   * Retrieves product IDs from the customer's cart.
   *
   * This method can be overridden to customize which cart products should be used
   * for recommendations (e.g., excluding gift cards, downloadable items, or sale items).
   *
   * @returns Promise resolving to array of product IDs
   *
   * @example
   * // Override to filter cart products
   * protected async getCartProductIds() {
   *   const products = await this.cartAPI.getProductsInCart();
   *   // Exclude gift cards and downloadable products
   *   return products
   *     .filter(p => !p.tags?.includes('gift-card'))
   *     .filter(p => !p.tags?.includes('downloadable'))
   *     .map(p => p.product_id);
   * }
   */
  protected async getCartProductIds(): Promise<number[]> {
    const products = await this.cartAPI.getProductsInCart();
    return products.map((product) => product.product_id);
  }

  /**
   * Retrieves the current product ID from the platform context.
   *
   * This method can be overridden to customize product page behavior,
   * such as using variant IDs instead of product IDs or including related products.
   *
   * @returns Promise resolving to array containing the current product ID
   *
   * @example
   * // Override to use variant ID instead
   * protected async getCurrentProductId() {
   *   const { product } = this.platformLoader.platform;
   *   const selectedVariant = this.getSelectedVariant();
   *   return selectedVariant?.id ? [selectedVariant.id] : [];
   * }
   */
  protected async getCurrentProductId(): Promise<(string | number)[]> {
    const { product } = this.platformLoader.platform;
    return product?.id ? [product.id] : [];
  }

  /**
   * Retrieves product IDs for a specific widget based on recommendation type and page context.
   *
   * This method orchestrates calls to specific product ID retrieval methods based on
   * the recommendation type and current page. Can be overridden for complex custom logic.
   *
   * @param widgetId - The widget identifier
   * @returns Promise resolving to array of product IDs
   *
   * @example
   * // Override to add custom product ID resolution
   * async getWidgetProductIds(widgetId) {
   *   const productIds = await super.getWidgetProductIds(widgetId);
   *   // Add complementary products for this shop
   *   return [...productIds, ...this.getComplementaryProducts(productIds)];
   * }
   */
  async getWidgetProductIds(widgetId: string): Promise<(string | number)[]> {
    const { recommendationType } = this.getRecommendationWidgetConfig(widgetId).params;
    const { router } = this.platformLoader.platform;

    if (recommendationType === 'recently-viewed') {
      return this.getRecentlyViewedProductIds();
    }

    if (router.page === 'cart') {
      return this.getCartProductIds();
    }

    if (router.page === 'product') {
      return this.getCurrentProductId();
    }

    return [];
  }

  /**
   * Retrieves the configuration for a specific recommendation widget.
   *
   * @param widgetId - The widget identifier
   * @returns Widget configuration object
   * @throws Error if widget ID is invalid for any supported page
   */
  getRecommendationWidgetConfig(widgetId: string) {
    const page = this.identifyWidgetPage({ widgetId });
    if (!page) {
      throw new Error(`Widget ID ${widgetId} is not valid for any supported page.`);
    }

    return this.appService.TAEAppConfig.recommendationWidgets[page][widgetId];
  }

  /**
   * Fetches recommendation products for a widget from the API.
   *
   * Builds request parameters and makes API call to retrieve recommended products.
   * Returns empty array if no products are found or if API call fails.
   *
   * @param widgetId - The widget identifier
   * @returns Promise resolving to array of recommended products
   */
  async getRecommendationWidgetProducts(widgetId: string) {
    const widgetConfig = this.getRecommendationWidgetConfig(widgetId);
    const { params: widgetRequestParams, widgetDesignSettings = {} } = widgetConfig;
    const { recommendationType, limit, shop } = widgetRequestParams;
    const { layoutDisplay = 'carousel' } = widgetDesignSettings;

    const customerId = this.platformLoader.platform.customer?.id;
    const collectionId = this.platformLoader.platform.collection?.id;
    const productIds = await this.getWidgetProductIds(widgetId);

    if (
      productIds.length === 0 &&
      ['frequently-bought-together', 'related-items', 'recently-viewed'].includes(
        recommendationType
      )
    ) {
      return [];
    }

    const params = {
      recommendationType,
      limit,
      shop,
      wid: widgetId,
      event_type: 'init',
      pg: this.platformLoader.platform.router.page,
      layoutDisplay,
    };

    const bodyPayload = {
      ...widgetRequestParams,
      layoutDisplay,
      wid: widgetId,
      productIds,
      collectionId,
      customerId,
      locale: this.platformLoader.platform.localization?.locale,
    };

    const { status, data } = await this.recommendationAPI.getRecommendation(params, bodyPayload);
    if (status !== 200) return [];

    return data.products || [];
  }

  /**
   * Transforms a single product variant from Shopify format.
   *
   * This method can be overridden to customize variant data transformation,
   * such as applying custom pricing, adding custom fields, or modifying image URLs.
   *
   * @param variant - Shopify product variant
   * @returns Transformed variant object
   *
   * @example
   * // Override to apply custom pricing
   * protected transformVariantData(variant) {
   *   const baseTransform = super.transformVariantData(variant);
   *   const customer = this.platformLoader.platform.customer;
   *   // Apply VIP discount
   *   if (customer?.tags?.includes('vip-member')) {
   *     baseTransform.price = baseTransform.price * 0.9;
   *     baseTransform.compare_at_price = variant.price / 100;
   *   }
   *   return baseTransform;
   * }
   */
  protected transformVariantData(variant: ShopifyProduct['variants'][0]) {
    const image = variant.featured_image?.src;
    const price = variant.price / 100;

    return {
      ...variant,
      price,
      image,
    };
  }

  /**
   * Rebuilds product data from Shopify format to Boost format.
   *
   * This method transforms Shopify product data structure to match the format
   * expected by the recommendation widget. Can be overridden to customize
   * the overall product transformation logic.
   *
   * @param product - Shopify product object
   * @returns Transformed product in Boost format
   *
   * @example
   * // Override to add custom product fields
   * rebuildProductDataFromShopifyData(product) {
   *   const baseProduct = super.rebuildProductDataFromShopifyData(product);
   *   // Add custom fields for this shop
   *   baseProduct.custom_badge = this.getProductBadge(product);
   *   baseProduct.shipping_info = this.getShippingInfo(product);
   *   return baseProduct;
   * }
   */
  rebuildProductDataFromShopifyData(product: ShopifyProduct): BoostProduct {
    const variants = product.variants.map((variant) => this.transformVariantData(variant));

    return {
      ...product,
      images_info: product.media,
      variants,
    } as unknown as BoostProduct;
  }

  /**
   * Builds the complete recommendation model for a widget.
   *
   * This is the main method that orchestrates widget data retrieval, validation,
   * and model construction. Returns undefined if widget validation fails.
   *
   * @param widgetId - The widget identifier
   * @returns Promise resolving to recommendation model or undefined if invalid
   */
  async getRecommendationModel(widgetId: string): Promise<RecommendationModel | undefined> {
    if (!this.validateWidget({ widgetId })) {
      return undefined;
    }

    const {
      widgetName,
      params: { recommendationType },
      widgetDesignSettings = {},
    } = this.getRecommendationWidgetConfig(widgetId);

    const products = await this.getRecommendationWidgetProducts(widgetId);
    const { product } = this.appService.TAEApp.dataObjects;

    if (widgetDesignSettings?.layoutDisplay === 'bundle' && Object.keys(product).length > 0) {
      products.unshift(this.rebuildProductDataFromShopifyData(product));
    }

    const { titleFontStyle } = widgetDesignSettings;
    if (titleFontStyle) {
      const { fontWeight, fontStyle } = this.appService.splitGoogleFontStyle(titleFontStyle);
      widgetDesignSettings.titleFontWeight = fontWeight;
      widgetDesignSettings.titleFontStyle = fontStyle;
    }

    const model = new RecommendationModel({
      widgetName,
      widgetId,
      products,
      recommendationType,
      page: this.identifyWidgetPage({ widgetId }) || 'home-page',
      widgetDesignSettings,
    });

    return model;
  }
}
